import produce from "immer"
import { ASC } from "const"
import { InferValueTypes } from "client/utils"
import {
    createAccountPermissions,
    createProjectPermissions,
} from "client/store/utils"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

const initAccountPermissionsState: types.AccountPermissionsState = {
    accountPermissions: createAccountPermissions().permissionsSectioned,
    baseProjectPermissions: createProjectPermissions().permissionsSectioned,

    users: {
        userIds: null,
        sortColumn: "firstName",
        sortOrder: ASC,
        filters: {
            status: null,
            search: null,
        },
        selected: null,
    },
    groups: {
        groupIds: null,
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
        selected: null,
    },
    projectGroups: {
        groupIds: null,
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
        selected: null,
    },

    reloadList: true,
}

const accountPermissionsReducer = produce((
    draft: types.AccountPermissionsState = initAccountPermissionsState,
    action: ActionTypes = null,
): types.AccountPermissionsState => {
    switch (action.type) {
        case types.SET_ACCOUNT_PERMISSIONS:
            draft.accountPermissions = action.accountPermissions
            draft.baseProjectPermissions = action.baseProjectPermissions
            return draft

        case types.UPDATE_ACCOUNT_PERMISSION: {
            const { section, permissionId, type, value } = action.data
            if (draft.accountPermissions[section]
                && draft.accountPermissions[section][permissionId]
                && draft.accountPermissions[section][permissionId][type] !== null) {
                draft.accountPermissions[section][permissionId][type] = value
            }
            return draft
        }

        case types.TOGGLE_ACCOUNT_PERMISSION_CUSTOMIZABLE: {
            const { section, permissionId } = action
            const permission = draft.accountPermissions[section] && draft.accountPermissions[section][permissionId]
                ? draft.accountPermissions[section][permissionId]
                : null
            if (permission) {
                draft.accountPermissions[section][permissionId].customizable = !permission.customizable
            }
            return draft
        }

        case types.UPDATE_BASE_PROJECT_PERMISSION: {
            const { section, permissionId, type, value } = action.data
            if (draft.baseProjectPermissions[section]
                && draft.baseProjectPermissions[section][permissionId]
                && draft.baseProjectPermissions[section][permissionId][type] !== null) {
                draft.baseProjectPermissions[section][permissionId][type] = value
            }
            return draft
        }

        case types.TOGGLE_BASE_PROJECT_PERMISSION_CUSTOMIZABLE: {
            const { section, permissionId } = action
            const permission = draft.baseProjectPermissions[section] && draft.baseProjectPermissions[section][permissionId]
                ? draft.baseProjectPermissions[section][permissionId]
                : null
            if (permission) {
                draft.baseProjectPermissions[section][permissionId].customizable = !permission.customizable
            }
            return draft
        }

        // not assigned users
        case types.ACCOUNT_PERMISSIONS_USER_IDS:
            draft.users.userIds = action.userIds
            return draft

        case types.ACCOUNT_PERMISSIONS_USER_SORT:
            draft.users.sortColumn = action.sortColumn
            draft.users.sortOrder = action.sortOrder
            return draft

        case types.ACCOUNT_PERMISSIONS_USER_FILTERS:
            draft.users.filters = action.filters
            return draft

        case types.ACCOUNT_PERMISSIONS_USER_SELECTED:
            draft.users.selected = action.selected
            return draft

        // not assigned groups
        case types.ACCOUNT_PERMISSIONS_GROUP_IDS:
            draft.groups.groupIds = action.groupIds
            return draft

        case types.ACCOUNT_PERMISSIONS_GROUP_SORT:
            draft.groups.sortColumn = action.sortColumn
            draft.groups.sortOrder = action.sortOrder
            return draft

        case types.ACCOUNT_PERMISSIONS_GROUP_FILTERS:
            draft.groups.filters = action.filters
            return draft

        case types.ACCOUNT_PERMISSIONS_GROUP_SELECTED:
            draft.groups.selected = action.selected
            return draft

        // not assigned project groups
        case types.ACCOUNT_PERMISSIONS_PROJECT_GROUP_IDS:
            draft.projectGroups.groupIds = action.groupIds
            return draft

        case types.ACCOUNT_PERMISSIONS_PROJECT_GROUP_SORT:
            draft.projectGroups.sortColumn = action.sortColumn
            draft.projectGroups.sortOrder = action.sortOrder
            return draft

        case types.ACCOUNT_PERMISSIONS_PROJECT_GROUP_FILTERS:
            draft.projectGroups.filters = action.filters
            return draft

        case types.ACCOUNT_PERMISSIONS_PROJECT_GROUP_SELECTED:
            draft.projectGroups.selected = action.selected
            return draft

        // reload state
        case types.ACCOUNT_PERMISSIONS_RELOAD_LIST:
            draft.reloadList = action.value
            return draft

        default:
            return draft
    }
})
export default accountPermissionsReducer
